import {FormControl, FormGroup, ValidationErrors} from '@angular/forms';
import {SiteWideUnlockCustomPath, SiteWideUnlockCustomPathChange} from '../../../../../model/ipop-configuration.model';

export const maximumCustomPathChanges = 100;
export type SiteWideUnlockPathForm = FormGroup<{
  saved: FormControl<SiteWideUnlockCustomPath | null>;
  path: FormControl<string>;
  selected: FormControl<boolean>;
  saveError: FormControl<string>;
}>;

/** Keeps saved identity with each draft so edits replace only that account's inclusion. */
export function createCustomPathForm(saved: SiteWideUnlockCustomPath | null = null): SiteWideUnlockPathForm
{
  const form: SiteWideUnlockPathForm = new FormGroup({
    saved: new FormControl<SiteWideUnlockCustomPath | null>(saved),
    path: new FormControl(saved?.path ?? '', {nonNullable: true}),
    selected: new FormControl(saved?.selected ?? true, {nonNullable: true}),
    saveError: new FormControl('', {nonNullable: true})
  });
  form.setValidators(() => validateCustomPath(form));
  form.updateValueAndValidity({emitEvent: false});

  return form;
}

/** Validates only new or edited inclusions; legacy paths may still be toggled by identity. */
function validateCustomPath(form: SiteWideUnlockPathForm): ValidationErrors | null
{
  let errors: ValidationErrors | null = null;
  const {saved, path, selected} = form.getRawValue();
  const changedPath = saved === null || path !== saved.path;
  if (selected && changedPath) {
    if (path.trim().length === 0) {
      errors = {pathInput: 'Enter a path or a URL on this account’s website.'};
    }
    if (/[?#\s]/u.test(path.trim())) {
      errors = {pathInput: 'Remove query strings, fragments, and spaces from the path.'};
    }
  }

  return errors;
}

/** Omits untouched rows and sends ID-only deselections without renaming the saved route. */
export function customPathChange(form: SiteWideUnlockPathForm): SiteWideUnlockCustomPathChange | null
{
  const {saved, path, selected} = form.getRawValue();
  let change: SiteWideUnlockCustomPathChange | null = null;
  if (saved === null && selected) {
    change = {path: path.trim(), selected: true};
  }
  if (saved !== null && (selected !== saved.selected || (selected && path !== saved.path))) {
    change = {accountAssignmentId: saved.accountAssignmentId, selected};
    if (selected && path !== saved.path) {
      change.path = path.trim();
    }
  }

  return change;
}

/** Requires complete saved identities before exposing options; discovery never implies selection. */
export function validCustomPathOptions(value: unknown): value is SiteWideUnlockCustomPath[]
{
  let valid = false;
  if (Array.isArray(value)) {
    valid = value.every(isSavedCustomPath)
      && new Set(value.map(row => row.accountAssignmentId)).size === value.length;
  }

  return valid;
}

/** Rejects partial or malformed response rows rather than manufacturing destructive defaults. */
function isSavedCustomPath(value: unknown): value is SiteWideUnlockCustomPath
{
  let valid = false;
  if (value !== null && typeof value === 'object') {
    const row = value as Record<string, unknown>;
    valid = typeof row.accountAssignmentId === 'number' && Number.isSafeInteger(row.accountAssignmentId)
      && row.accountAssignmentId > 0 && typeof row.path === 'string' && row.path.trim().length > 0
      && typeof row.selected === 'boolean' && (row.pageType === null || typeof row.pageType === 'string');
  }

  return valid;
}
